import React, { createContext, useState, useEffect, useContext } from 'react';

const AuthContext = createContext(null);

// Hash password using SHA-256 via Web Crypto API
const hashPassword = async (password) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in from localStorage
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    const username = localStorage.getItem('username');
    const userData = localStorage.getItem('userData');
    
    if (isLoggedIn && username) {
      let userDetails = { username };
      
      if (userData) {
        try {
          const parsedData = JSON.parse(userData);
          userDetails = { ...userDetails, ...parsedData };
        } catch (error) {
          // Ignore corrupt user data
        }
      }
      
      setCurrentUser(userDetails);
    }
    
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    // Get client accounts from localStorage
    const clientAccounts = JSON.parse(localStorage.getItem('clientAccounts') || '[]');

    // Hash the provided password and compare against stored hash
    const passwordHash = await hashPassword(password);
    const userAccount = clientAccounts.find(account =>
      account.username === username && account.passwordHash === passwordHash
    );

    return new Promise((resolve) => {
      setTimeout(() => {
        if (userAccount) {
          // Login successful
          localStorage.setItem('isLoggedIn', 'true');
          localStorage.setItem('username', username);
          localStorage.setItem('userData', JSON.stringify({
            fullName: userAccount.fullName,
            organization: userAccount.organization
          }));

          setCurrentUser({
            username,
            fullName: userAccount.fullName,
            organization: userAccount.organization
          });

          resolve({ success: true });
        } else {
          // Login failed
          resolve({ success: false, message: 'Invalid username or password' });
        }
      }, 1000);
    });
  };

  const register = async (username, password, fullName, organization, invitationCode) => {
    // Get client accounts from localStorage to check for duplicates
    const clientAccounts = JSON.parse(localStorage.getItem('clientAccounts') || '[]');

    // Check if username already exists
    const usernameExists = clientAccounts.some(account => account.username === username);

    // Validate invitation code
    if (!validateInvitationCode(invitationCode)) {
      return { success: false, message: 'Invalid or expired invitation code' };
    }

    // Check for duplicate username
    if (usernameExists) {
      return { success: false, message: 'Username already exists' };
    }

    // Mark invitation code as used
    markInvitationCodeAsUsed(invitationCode);

    // Hash the password before storing
    const passwordHash = await hashPassword(password);

    // Create new client account (no plaintext password stored)
    const newAccount = {
      id: Date.now().toString(),
      username,
      passwordHash,
      fullName,
      organization,
      createdAt: new Date().toISOString()
    };

    // Store account in localStorage
    clientAccounts.push(newAccount);
    localStorage.setItem('clientAccounts', JSON.stringify(clientAccounts));

    return { success: true };
  };

  // Function to validate invitation code
  const validateInvitationCode = (code) => {
    // Get all valid codes from localStorage
    const storedCodes = JSON.parse(localStorage.getItem('invitationCodes') || '[]');
    const usedCodes = JSON.parse(localStorage.getItem('usedInvitationCodes') || '[]');
    
    // Check if the code exists and hasn't been used
    return storedCodes.some(storedCode => 
      storedCode.code === code && 
      !usedCodes.includes(code) &&
      new Date(storedCode.expiresAt) > new Date()
    );
  };

  // Function to mark invitation code as used
  const markInvitationCodeAsUsed = (code) => {
    const usedCodes = JSON.parse(localStorage.getItem('usedInvitationCodes') || '[]');
    
    if (!usedCodes.includes(code)) {
      usedCodes.push(code);
      localStorage.setItem('usedInvitationCodes', JSON.stringify(usedCodes));
    }
  };

  const logout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('username');
    localStorage.removeItem('userData');
    setCurrentUser(null);
  };

  const value = {
    currentUser,
    loading,
    login,
    register,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export default AuthContext;
